import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDrop, useDrag } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import ControlRenderer from './ControlRenderer';
import '../POTemplate.css';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';

const getDefaultOptions = (controlType) => {
  if (['Dropdown', 'Checkbox', 'RadioButton'].includes(controlType)) {
    return [
      { id: Date.now(), name: 'Option 1' },
      { id: Date.now() + 1, name: 'Option 2' },
      { id: Date.now() + 2, name: 'Option 3' },
    ];
  }
  return [];
};

const getDefaultPlaceholder = (controlType, label, t) => {
  const baseLabel = label || controlType;

  switch (controlType) {
    case 'Textbox':
    case 'TextArea':
      return `${t('COMMON_LABEL.ENTER')} ${baseLabel}`;
    case 'Dropdown':
      return `${t('COMMON_LABEL.CHECK')} ${baseLabel}`;
    case 'Checkbox':
    case 'RadioButton':
      return `${t('COMMON_LABEL.SELECT')} ${baseLabel}`;
    case 'DatePicker':
      return `${t('COMMON_LABEL.SELECT')} ${baseLabel}`;
    default:
      return `${t('COMMON_LABEL.ENTER')} ${baseLabel}`;
  }
};

export default function Section({ section, isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { setDetailState, bulkUpdateSections, detailState } = usePOTemplateDnd();

  const [localSection, setLocalSection] = useState(section);
  const [isEditing, setIsEditing] = useState(false);
  const [sectionName, setSectionName] = useState(section?.name || `Section ${section?.id}`);

  useEffect(() => {
    setLocalSection(section);
    setSectionName(section?.name || `Section ${section?.id}`);
  }, [section]);

  const syncWithContext = useCallback(
    (updatedSection) => {
      console.log('🔄 syncWithContext called with:', {
        updatedSectionId: updatedSection.id,
        updatedSectionColumns: updatedSection.columns?.length,
        currentSectionsCount: detailState.sections?.length,
      });

      const currentSections = detailState.sections || [];
      if (!Array.isArray(currentSections) || currentSections.length === 0) {
        console.warn('No valid sections found in detailState');
        return;
      }

      const sectionIndex = currentSections.findIndex((s) => s.id === updatedSection.id);
      if (sectionIndex === -1) {
        console.warn(`Section with id ${updatedSection.id} not found`);
        return;
      }

      console.log('📝 Before update:', {
        sectionIndex,
        originalSection: currentSections[sectionIndex],
        updatedSection,
        totalSections: currentSections.length,
      });

      const updatedSections = [...currentSections];
      updatedSections[sectionIndex] = updatedSection;

      if (updatedSections.length !== currentSections.length) {
        console.error('Section count mismatch during sync');
        return;
      }
      bulkUpdateSections(updatedSections);
    },
    [detailState.sections, bulkUpdateSections],
  );

  const handleLocalControlMove = useCallback(
    (moveOperation) => {
      setLocalSection((prevSection) => {
        if (!prevSection || !prevSection.id) {
          return prevSection;
        }

        const newSection = { ...prevSection };
        newSection.columns = [...(prevSection.columns || [])];

        switch (moveOperation.type) {
          case 'ADD_CONTROL': {
            const { columnIdx, position, control } = moveOperation;
            const newControls = [...(newSection.columns[columnIdx] || [])];
            newControls.splice(position, 0, control);
            newSection.columns[columnIdx] = newControls;
            break;
          }

          case 'MOVE_CONTROL_SAME_COLUMN': {
            const { columnIdx, fromIndex, toIndex } = moveOperation;
            const controls = [...(newSection.columns[columnIdx] || [])];
            const [movedControl] = controls.splice(fromIndex, 1);
            controls.splice(toIndex, 0, movedControl);
            newSection.columns[columnIdx] = controls;
            break;
          }

          case 'MOVE_CONTROL_CROSS_COLUMN': {
            const { fromColumnIdx, toColumnIdx, fromIndex, toPosition, newControlId } = moveOperation;

            const sourceControls = [...(newSection.columns[fromColumnIdx] || [])];
            const [controlToMove] = sourceControls.splice(fromIndex, 1);
            newSection.columns[fromColumnIdx] = sourceControls;

            const targetControls = [...(newSection.columns[toColumnIdx] || [])];
            const movedControl = { ...controlToMove, id: newControlId };
            targetControls.splice(toPosition, 0, movedControl);
            newSection.columns[toColumnIdx] = targetControls;
            break;
          }

          case 'DELETE_CONTROL': {
            const { columnIdx, controlIndex } = moveOperation;
            const controls = [...(newSection.columns[columnIdx] || [])];
            controls.splice(controlIndex, 1);
            newSection.columns[columnIdx] = controls;
            break;
          }

          case 'UPDATE_CONTROL_VALUE': {
            const { columnIdx, controlIndex, value } = moveOperation;
            const controls = [...(newSection.columns[columnIdx] || [])];
            if (controls[controlIndex]) {
              controls[controlIndex] = { ...controls[controlIndex], value };
              newSection.columns[columnIdx] = controls;
            }
            break;
          }
          default:
            // Unknown operation type
            break;
        }

        // Validate section structure before returning
        if (!newSection.id || !Array.isArray(newSection.columns)) {
          console.error('Invalid section structure detected, reverting changes');
          return prevSection; // Return original if corrupted
        }

        // Sync with context immediately - no timeout needed
        try {
          syncWithContext(newSection);
        } catch (error) {
          console.error('Error syncing section with context:', error);
          return prevSection; // Return original if sync fails
        }

        return newSection;
      });
    },
    [syncWithContext],
  );

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    const updatedSection = { ...localSection, name: sectionName };
    setLocalSection(updatedSection);

    syncWithContext(updatedSection);

    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSectionName(localSection?.name || `Section ${localSection?.id}`);
  };

  const handleDelete = () => {
    setDetailState((oldData) => ({
      ...oldData,
      showDeleteSectionModal: true,
      deleteSectionId: localSection?.id,
      deleteSectionName: localSection?.name,
    }));
  };

  return (
    <div className={`inner-container p-10 potemplate-section potemplate-columns-${localSection?.columns?.length || 1}`}>
      <div className="section-template-title potemplate-section-header mb-0">
        <div className="potemplate-section-title">
          {isEditing && !isPreviewMode ? (
            <input type="text" value={sectionName} onChange={(e) => setSectionName(e.target.value)} className="potemplate-section-name-input" />
          ) : (
            sectionName
          )}
        </div>
        {!isPreviewMode && (
          <div className="potemplate-section-actions">
            {isEditing ? (
              <div className="d-flex ml-auto">
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
                  <button type="button" aria-label={t('COMMON_LABEL.SAVE')} onClick={handleSave} className="potemplate-section-edit-btn">
                    <i className="icon-Tick-1 f-24" />
                  </button>
                </CustomTooltip>
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.CANCEL')}>
                  <button type="button" aria-label={t('COMMON_LABEL.CANCEL')} onClick={handleCancel} className="potemplate-section-edit-btn">
                    <i className="icon-close" />
                  </button>
                </CustomTooltip>
              </div>
            ) : (
              <div className="d-flex ml-auto">
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.EDIT')}>
                  <button type="button" aria-label={t('COMMON_LABEL.EDIT')} onClick={handleEdit} className="potemplate-section-edit-btn">
                    <i className="icon-Edit-1 f-20" />
                  </button>
                </CustomTooltip>
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.DELETE')}>
                  <button type="button" aria-label={t('COMMON_LABEL.DELETE')} onClick={handleDelete} className="potemplate-section-delete-btn">
                    <i className="icon-Delete f-22" />
                  </button>
                </CustomTooltip>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section Columns */}
      <div className="potemplate-section-content">
        {localSection?.columns?.map((controls, colIdx) => (
          <SectionColumn
            key={`section-${localSection.id}-column-${colIdx}`}
            controls={controls}
            sectionId={localSection.id}
            colIdx={colIdx}
            isPreviewMode={isPreviewMode}
            onControlMove={handleLocalControlMove}
          />
        ))}
      </div>
    </div>
  );
}

function SectionColumn({ controls, sectionId, colIdx, isPreviewMode = false, onControlMove }) {
  const { t } = useTranslation('purchase-order');

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['CONTROL', 'EXISTING_CONTROL'],
    drop: (item, monitor) => {
      if (monitor.didDrop() || isPreviewMode || !onControlMove) {
        return;
      }

      try {
        if (item.type === 'EXISTING_CONTROL') {
          if (!item.sourceSectionId || item.sourceColumnIdx === undefined || item.sourceIndex === undefined) {
            console.error('Invalid EXISTING_CONTROL item:', item);
            return;
          }

          if (item.sourceSectionId === sectionId && item.sourceColumnIdx === colIdx) {
            onControlMove({
              type: 'MOVE_CONTROL_SAME_COLUMN',
              columnIdx: colIdx,
              fromIndex: item.sourceIndex,
              toIndex: controls?.length || 0,
            });
          } else {
            onControlMove({
              type: 'MOVE_CONTROL_CROSS_COLUMN',
              fromColumnIdx: item.sourceColumnIdx,
              toColumnIdx: colIdx,
              fromIndex: item.sourceIndex,
              toPosition: controls?.length || 0,
              newControlId: Date.now() + Math.random() + Math.random(),
            });
          }
        } else {
          if (!item.type) {
            console.error('Invalid control type:', item);
            return;
          }

          const newControl = {
            id: Date.now() + Math.random() + Math.random(),
            type: item.type || 'Textbox',
            label: item.label || `${item.type || 'Control'} Label`,
            value: item.value || '',
            required: item.required || false,
            placeholder: getDefaultPlaceholder(item.type, item.label, t),
            options: ['Dropdown', 'Checkbox', 'RadioButton'].includes(item.type) ? getDefaultOptions(item.type) : [],
            ...(item.type === 'Textbox' && { characterLimit: 50 }),
            ...item,
          };

          onControlMove({
            type: 'ADD_CONTROL',
            columnIdx: colIdx,
            position: controls?.length || 0,
            control: newControl,
          });
        }
      } catch (error) {
        console.error('Error during drop operation:', error);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }) && !isPreviewMode,
      canDrop: monitor.canDrop() && !isPreviewMode,
    }),
  });

  return (
    <div ref={drop} className={`potemplate-section-column${isOver && canDrop && !isPreviewMode ? ' potemplate-drop-preview' : ''}`}>
      {!isPreviewMode && <ControlDropZone sectionId={sectionId} columnIdx={colIdx} position={0} t={t} onControlMove={onControlMove} />}

      {!isPreviewMode && controls?.length === 0 && isOver && canDrop && (
        <div
          className="potemplate-drop-zone potemplate-drop-active"
          style={{
            minHeight: '40px',
            transition: 'min-height 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {t('PO_TEMPLATE.DETAIL_PAGE.DROP_CONTROL_HERE')}
        </div>
      )}

      {controls?.map((ctrl, idx) => (
        <React.Fragment key={ctrl.id}>
          <DraggableControl
            control={ctrl}
            sectionId={sectionId}
            columnIdx={colIdx}
            controlIdx={idx}
            isPreviewMode={isPreviewMode}
            onControlMove={onControlMove}
          />
          {!isPreviewMode && <ControlDropZone sectionId={sectionId} columnIdx={colIdx} position={idx + 1} t={t} onControlMove={onControlMove} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function DraggableControl({ control, sectionId, columnIdx, controlIdx, isPreviewMode = false, onControlMove }) {
  const safeControl = {
    id: control?.id || `control-${Date.now()}`,
    type: control?.type || 'Textbox',
    label: control?.label || 'Untitled Control',
    value: control?.value || '',
    ...control,
  };

  const [{ isDragging }, drag] = useDrag({
    type: 'EXISTING_CONTROL',
    item: () => {
      return {
        type: 'EXISTING_CONTROL',
        control: safeControl,
        sourceIndex: controlIdx,
        sourceColumnIdx: columnIdx,
        sourceSectionId: sectionId,
      };
    },
    canDrag: !isPreviewMode,
    end: () => {},
    collect: (monitor) => ({
      isDragging: monitor.isDragging() && !isPreviewMode,
    }),
  });

  if (!control || typeof control !== 'object') {
    return (
      <div className="control-container error-control">
        <div className="alert alert-danger">Invalid control data</div>
      </div>
    );
  }

  try {
    return (
      <div ref={!isPreviewMode ? drag : null} className={`control-container ${isDragging && !isPreviewMode ? 'control-dragging' : ''}`}>
        <ControlRenderer
          control={safeControl}
          sectionId={sectionId}
          columnIdx={columnIdx}
          controlIdx={controlIdx}
          value={safeControl.value || ''}
          isPreviewMode={isPreviewMode}
          onChange={(value) => {
            if (!isPreviewMode && onControlMove) {
              onControlMove({
                type: 'UPDATE_CONTROL_VALUE',
                columnIdx,
                controlIndex: controlIdx,
                value,
              });
            }
          }}
        />
      </div>
    );
  } catch (error) {
    return (
      <div className="control-container error-control">
        <div className="alert alert-danger">Control render error: {error.message}</div>
      </div>
    );
  }
}

function ControlDropZone({ sectionId, columnIdx, position, t, isPreviewMode = false, onControlMove }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['CONTROL', 'EXISTING_CONTROL'],
    drop: (item, monitor) => {
      if (monitor.didDrop() || isPreviewMode || !onControlMove) {
        return;
      }

      try {
        if (item.type === 'EXISTING_CONTROL') {
          if (!item.sourceSectionId || item.sourceColumnIdx === undefined || item.sourceIndex === undefined) {
            console.error('Invalid EXISTING_CONTROL item in ControlDropZone:', item);
            return;
          }

          if (item.sourceSectionId === sectionId && item.sourceColumnIdx === columnIdx) {
            let targetIndex = position;
            if (item.sourceIndex < position) {
              targetIndex = position - 1;
            }

            if (targetIndex !== item.sourceIndex) {
              onControlMove({
                type: 'MOVE_CONTROL_SAME_COLUMN',
                columnIdx,
                fromIndex: item.sourceIndex,
                toIndex: targetIndex,
              });
            }
          } else {
            onControlMove({
              type: 'MOVE_CONTROL_CROSS_COLUMN',
              fromColumnIdx: item.sourceColumnIdx,
              toColumnIdx: columnIdx,
              fromIndex: item.sourceIndex,
              toPosition: position,
              newControlId: Date.now() + Math.random() + Math.random(),
            });
          }
        } else {
          if (!item.type) {
            console.error('Invalid control type in ControlDropZone:', item);
            return;
          }

          const newControl = {
            id: Date.now() + Math.random() + Math.random(),
            type: item.type || 'Textbox',
            label: item.label || `${item.type || 'Control'} Label`,
            value: item.value || '',
            required: item.required || false,
            placeholder: getDefaultPlaceholder(item.type, item.label, t),
            options: ['Dropdown', 'Checkbox', 'RadioButton'].includes(item.type) ? getDefaultOptions(item.type) : [],
            ...(item.type === 'Textbox' && { characterLimit: 50 }),
            ...item,
          };

          onControlMove({
            type: 'ADD_CONTROL',
            columnIdx,
            position,
            control: newControl,
          });
        }
      } catch (error) {
        console.error('Error during drop operation in ControlDropZone:', error);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }) && !isPreviewMode,
      canDrop: monitor.canDrop() && !isPreviewMode,
    }),
  });

  if (isPreviewMode) {
    return null;
  }

  return (
    <div
      ref={drop}
      className={`potemplate-drop-zone${isOver && canDrop ? ' potemplate-drop-active' : ''}`}
      style={{
        minHeight: isOver && canDrop ? '40px' : '8px',
        transition: 'min-height 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isOver && canDrop && <div className="potemplate-drop-hint">{t('PO_TEMPLATE.DETAIL_PAGE.DROP_CONTROL_HERE')}</div>}
    </div>
  );
}
Section.propTypes = {
  section: PropTypes.shape({}).isRequired,
  isPreviewMode: PropTypes.bool,
};

Section.defaultProps = {
  isPreviewMode: false,
};
